/**
 * Kleurwisselende achtergrond bij het klikken op een andere kleurvariant.
 * Werkt alleen op MagSafe Ring en Thermosbeker productpagina's (elk apart product,
 * gekoppeld via kleurstalen). Onderschept de klik, haalt de doelpagina op via fetch,
 * vervangt de inhoud van de product-form sectie zonder volledige page reload,
 * en animeert de achtergrondkleur.
 */
(() => {
  const COLOR_MAP = {
    'hydroblue-magsafe-ring-black-stone': '#e9e7e2',
    'hydroblue-magsafe-ring-blue-mist': '#dceaf5',
    'hydroblue-magsafe-ring-soft-sand': '#f3ebdd',
    'hydroblue-magsafe-ring-pink-cloud': '#fbe7ec',
    'magsafe-ring-crystal-blue': '#e1f0f5',
    'hydroblue-soft-cream-thermal-mug': '#f5eee0',
    'hydroblue-midnight-thermal-mug': '#e3e6ef'
  };

  const ELIGIBLE_PATTERN = /\/products\/(hydroblue-magsafe-ring-[a-z-]+|magsafe-ring-crystal-blue|hydroblue-soft-cream-thermal-mug|hydroblue-midnight-thermal-mug)(\/|$|\?)/;

  function isEligible(pathOrUrl) {
    try {
      const path = new URL(pathOrUrl, window.location.origin).pathname;
      return ELIGIBLE_PATTERN.test(path);
    } catch (e) {
      return false;
    }
  }

  function handleFromPath(pathOrUrl) {
    try {
      const path = new URL(pathOrUrl, window.location.origin).pathname;
      const match = path.match(/\/products\/([a-z0-9-]+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  // De kleurstalen (swatches) worden NIET binnen de product-form sectie zelf
  // gerenderd, maar door een losse Section Store app-sectie elders op de
  // pagina (met een eigen <template id="ss-product-variants-template-...">).
  // Een fetch van alleen de product-form sectie bevat die template dus niet;
  // we moeten 'm apart meenemen via de sections=a,b variant van de Section
  // Rendering API. De section-id is stabiel per template (gedeeld door alle
  // producten die dat template gebruiken), dus eenmalig detecteren bij init
  // is voldoende.
  let ssVariantSectionId = null;

  // De titel-suffix (bv. "Hydroblue") wordt eenmalig bij init vastgelegd,
  // zodat opeenvolgende swaps 'm niet steeds cumulatief opnieuw uit de
  // (al gewijzigde) huidige titel afleiden.
  let titleSuffix = null;

  function detectSsVariantSectionId() {
    const tpl = document.querySelector('template[id^="ss-product-variants-template-"]');
    if (!tpl) return null;
    const sectionDiv = tpl.closest('[id^="shopify-section-"]');
    return sectionDiv ? sectionDiv.id.replace('shopify-section-', '') : null;
  }

  function getSectionWrapper() {
    return document.querySelector('[id^="shopify-section-"][id$="__product-form"]');
  }

  function sectionIdFromWrapper(wrapper) {
    return wrapper.id.replace('shopify-section-', '');
  }

  function extractInner(raw) {
    // raw = <div id="shopify-section-...">...(inner content)...</div>
    // strip the outer wrapper by slicing the raw string directly (avoids a
    // parse -> serialize -> re-parse round trip, which corrupts deduped
    // <style> tags) and drop <style> tags (see stripDedupedStyleTags).
    const firstGt = raw.indexOf('>');
    const lastCloseDiv = raw.lastIndexOf('</div>');
    if (firstGt === -1 || lastCloseDiv === -1) return null;
    return stripDedupedStyleTags(raw.slice(firstGt + 1, lastCloseDiv));
  }

  function stripDedupedStyleTags(html) {
    // Shopify's {% style %} tag deduplicates identical <style> blocks page-wide.
    // When a section is fetched and reinserted in isolation, that dedup reference
    // can break. The specific blocks affected here (mobile/desktop media visibility)
    // are product-independent and already covered statically in custom.css, so it's
    // safe to strip all <style> tags from the swapped-in fragment.
    return html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  }

  function applyBackground(handle) {
    const wrapper = getSectionWrapper();
    const color = handle && COLOR_MAP[handle];
    if (!wrapper || !color) return;
    wrapper.style.backgroundColor = color;
  }

  function executeScripts(container) {
    container.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  function bindSwatchLinks() {
    const wrapper = getSectionWrapper();
    if (!wrapper) return;
    wrapper.querySelectorAll('a[href^="/products/"]').forEach((a) => {
      a.removeEventListener('click', onSwatchClick);
      a.addEventListener('click', onSwatchClick);
    });
  }

  async function swapProduct(url, pushState) {
    const wrapper = getSectionWrapper();
    if (!wrapper) {
      window.location.href = url;
      return;
    }

    wrapper.classList.add('color-swap-loading');

    try {
      const sectionId = sectionIdFromWrapper(wrapper);
      const ids = [sectionId];
      if (ssVariantSectionId) ids.push(ssVariantSectionId);
      const sep = url.includes('?') ? '&' : '?';
      const sectionsUrl = url + sep + 'sections=' + encodeURIComponent(ids.join(','));

      const res = await fetch(sectionsUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Netwerkfout bij ophalen productsectie');
      const data = await res.json();

      const rawMain = data[sectionId];
      if (!rawMain) throw new Error('Onverwacht antwoord van sectie-API');
      const inner = extractInner(rawMain);
      if (inner === null) throw new Error('Onverwacht antwoord van sectie-API');

      // Grab the swatch template markup from the separately fetched
      // ss-product-variant section so the color swatches can be re-inserted
      // into the fresh placeholder div.
      let swatchTemplateMatch = null;
      if (ssVariantSectionId && data[ssVariantSectionId]) {
        swatchTemplateMatch = data[ssVariantSectionId].match(/<template id="ss-product-variants-template-[^"]*"[^>]*>([\s\S]*?)<\/template>/);
      }

      wrapper.innerHTML = inner;
      executeScripts(wrapper);

      // Nieuw ingevoegde elementen zijn nooit door de scroll-reveal
      // IntersectionObserver gezien, dus blijven anders permanent
      // opacity:0 (thema-CSS-regel ".js .show-on-scroll { opacity: 0; }").
      wrapper.querySelectorAll('.show-on-scroll').forEach((el) => {
        el.style.opacity = '1';
      });

      // De product-media Swiper-carousel initialiseert alleen bij het
      // origineel geladen element; op nieuw ingevoegde HTML moet dit
      // handmatig getriggerd worden, anders blijft de hoofdafbeelding
      // onzichtbaar (opacity:0 tot Swiper init draait).
      const productMedia = wrapper.querySelector('product-media');
      if (productMedia && typeof productMedia.initSlider === 'function') {
        try {
          productMedia.initSlider();
        } catch (e) {
          /* stil negeren; slider blijft dan inert maar afbeelding is al zichtbaar */
        }
      }

      const placeholder = wrapper.querySelector('#ss-product-variants');
      if (placeholder && swatchTemplateMatch) {
        const temp = document.createElement('div');
        temp.innerHTML = swatchTemplateMatch[1];
        if (temp.firstElementChild) {
          placeholder.insertAdjacentElement('afterend', temp.firstElementChild);
        }
      }

      const handle = handleFromPath(url);
      applyBackground(handle);

      if (pushState) {
        history.pushState({ colorSwap: true }, '', url);
      }

      const heading = wrapper.querySelector('h1');
      if (heading && heading.textContent.trim()) {
        document.title = titleSuffix
          ? heading.textContent.trim() + ' – ' + titleSuffix
          : heading.textContent.trim();
      }

      bindSwatchLinks();
    } catch (err) {
      window.location.href = url;
    } finally {
      wrapper.classList.remove('color-swap-loading');
    }
  }

  function onSwatchClick(event) {
    const link = event.target.closest('a[href^="/products/"]');
    if (!link) return;
    const url = link.getAttribute('href');
    if (!url || !isEligible(url)) return;

    event.preventDefault();
    swapProduct(url, true);
  }

  function init() {
    if (!isEligible(window.location.pathname)) return;

    const wrapper = getSectionWrapper();
    if (wrapper) {
      wrapper.classList.add('color-swap-enabled');
    }

    ssVariantSectionId = detectSsVariantSectionId();

    const initialParts = document.title.split(/\s[–-]\s/);
    titleSuffix = initialParts.length > 1 ? initialParts.slice(-1)[0] : null;

    applyBackground(handleFromPath(window.location.pathname));
    bindSwatchLinks();

    window.addEventListener('popstate', () => {
      if (isEligible(window.location.pathname)) {
        swapProduct(window.location.pathname + window.location.search, false);
      }
    });
  }

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }
})();

